import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProjectAndCustomer } from '../project/projectType';
import InvoiceApi from './invoiceApi';
import { InvoiceProjectCustomer, ItemizedInvoice, InvoiceTotals } from './invoiceType';
import ProjectApi from '../project/projectApi';
import { Trash } from 'lucide-react';
import HorizontalMenu from '../components/common/horizontalMenu';
import { formatAddress } from '../lib/global_function';
import { formatCurrency } from '../lib/global_function';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';

const DeleteInvoicePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Invoice', path: '/main/project/invoice' },
    { label: 'Delete', path: '/main/project/invoice/delete' },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const invoice: InvoiceProjectCustomer | undefined = location.state?.invoice;
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [itemizedInvoices, setItemizedInvoices] = useState<ItemizedInvoice[]>([]);

  // Check if invoice is available
  useEffect(() => {
    if (!invoice) {
      setErrorMessage('Invoice not available, please select an invoice by clicking Go To Invoice button.');
    }
  }, [invoice]);

  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      try {
        const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        setProjectsAndCustomers(fetchedProjectsAndCustomers);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };

    fetchProjectsAndCustomers();
  }, [invoice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;

    setErrorMessage('');
    setMessage('');

    try {
      await InvoiceApi.deleteInvoice(invoice.invoice_id);
      setMessage('Invoice deleted successfully');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleNavigateToInvoiceList = () => {
    navigate('/main/project/invoice');
  };

  const projectAndCustomer = invoice
    ? projectsAndCustomers.find(
        (pc) => pc.project_id === invoice.project_id && pc.customer_id === invoice.customer_id
      )
    : undefined;

  const formattedAddress = projectAndCustomer
    ? formatAddress(
        projectAndCustomer.customer_address_1,
        '',
        projectAndCustomer.customer_city,
        projectAndCustomer.customer_state,
        projectAndCustomer.customer_zip
      )
    : '';

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Delete Invoice</h2>

        {errorMessage && (
          <div className="space-y-4">
            <Alert variant="error">{errorMessage}</Alert>
            {!invoice && (
              <Button onClick={handleNavigateToInvoiceList} size="medium">
                Go To Invoice
              </Button>
            )}
          </div>
        )}

        {message ? (
          <div className="space-y-4">
            <Alert variant="success">{message}</Alert>
            <Button onClick={handleNavigateToInvoiceList} size="medium">
              Return to Invoices
            </Button>
          </div>
        ) : invoice ? (
          <form onSubmit={handleSubmit} className="max-w-5xl">
            <div className="space-y-6">
              {/* Project and Customer Information */}
              <fieldset className="border-2 border-gray-300 rounded-lg p-6">
                <legend className="text-lg font-semibold px-2">Project and Customer Information</legend>
                <div className="space-y-3">
                  <div>
                    <span className="font-semibold">Project:</span>
                    <span className="ml-6 text-paragraph">
                      {projectAndCustomer?.name} - {projectAndCustomer?.project_type}
                      {projectAndCustomer?.project_description && (
                        <span>, {projectAndCustomer.project_description.substring(0, 100)}</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Customer:</span>
                    <span className="ml-6 text-paragraph">
                      {projectAndCustomer?.customer_first_name} {projectAndCustomer?.customer_last_name},{' '}
                      {projectAndCustomer?.customer_phone}, {projectAndCustomer?.customer_email},{' '}
                      {formattedAddress}
                    </span>
                  </div>
                </div>
              </fieldset>

              {/* Invoice Details */}
              <fieldset className="border-2 border-gray-300 rounded-lg p-6">
                <legend className="text-lg font-semibold px-2">Invoice Details</legend>
                <div className="space-y-4">
                  <div>
                    <span className="font-semibold">Name:</span>
                    <span className="ml-4 text-paragraph">{invoice.name}</span>
                  </div>

                  <div>
                    <span className="font-semibold">Terms and Conditions:</span>
                    <span className="ml-4 text-paragraph">{invoice.payment_instruction}</span>
                  </div>

                  {/* Financial Summary */}
                  <div className="mt-5 bg-gray-100 p-4 rounded-lg">
                    <div className="font-semibold mb-3">Financial Summary</div>
                    <div className="space-y-2">
                      <div>
                        <span className="font-semibold">Subtotal (Before Tax):</span>
                        <span className="ml-4 text-paragraph">
                          ${formatCurrency(Number(invoice.sub_total_sum_lineitems).toFixed(2))}
                        </span>
                      </div>

                      <div>
                        <span className="font-semibold">Tax Rate:</span>
                        <span className="ml-4 text-paragraph">{Number(invoice.tax_rate).toFixed(2)}%</span>
                      </div>

                      <div>
                        <span className="font-semibold">Tax Amount:</span>
                        <span className="ml-4 text-paragraph">
                          ${formatCurrency(Number(invoice.tax_amount).toFixed(2))}
                        </span>
                      </div>

                      <div>
                        <span className="font-semibold">Total Amount:</span>
                        <span className="ml-4 text-paragraph">
                          ${formatCurrency(Number(invoice.final_total).toFixed(2))}
                        </span>
                      </div>

                      <div>
                        <span className="font-semibold">Paid Amount:</span>
                        <span className="ml-4 text-paragraph">
                          ${formatCurrency(Number(invoice.amount_paid).toFixed(2))}
                        </span>
                      </div>

                      <div>
                        <span className="font-semibold">Balance Due:</span>
                        <span className="ml-4 text-paragraph">
                          ${formatCurrency(Number(invoice.final_total - invoice.amount_paid).toFixed(2))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Itemized Invoices */}
                  {itemizedInvoices.length > 0 && (
                    <div className="mt-5">
                      <div className="font-semibold mb-3">Itemized Invoices:</div>
                      <div className="space-y-3">
                        {itemizedInvoices.map((item) => (
                          <div
                            key={item.itemized_invoice_id}
                            className="ml-4 bg-gray-50 p-4 rounded-lg space-y-2"
                          >
                            <div>
                              <span className="font-semibold">Code:</span>
                              <span className="ml-2 text-paragraph">{item.code}</span>
                            </div>
                            <div>
                              <span className="font-semibold">Description:</span>
                              <span className="ml-2 text-paragraph">{item.description}</span>
                            </div>
                            <div>
                              <span className="font-semibold">Amount:</span>
                              <span className="ml-2 text-paragraph">
                                ${formatCurrency(Number(item.amount_dollar).toFixed(2))}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>

              {/* Warning Message */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <Alert variant="warning">
                  Warning: If you delete this invoice, all associated itemized invoices will be permanently
                  deleted as well.
                </Alert>
              </div>

              {/* Delete Button */}
              <div>
                <Button
                  type="submit"
                  size="medium"
                  className="bg-red-600 hover:bg-red-700 active:bg-red-800"
                >
                  <Trash size={16} />
                  Delete Invoice
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <div className="mt-5">
            <p className="text-gray-600">No invoice data available to display.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default DeleteInvoicePage;